/**
 * 企业大脑 — 登录页面
 * 支持多租户：根据 URL 显示租户名称，支持游客登录
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePermissionStore } from '@/stores/permissionStore';
import { checkTenantExists, type TenantCheckResult } from '@/services/tenant';
import './LoginPage.css';

type ThemePreference = 'system' | 'light' | 'dark';

function getInitialTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem('eb-theme-pref');
    if (stored === 'system' || stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch { /* ignore */ }
  return 'system';
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  return pref === 'system' ? getSystemTheme() : pref;
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 租户信息
  const [tenantInfo, setTenantInfo] = useState<TenantCheckResult | null>(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  
  // 主题
  const [themePreference, setThemePreference] = useState<ThemePreference>(getInitialTheme);
  const resolvedTheme = resolveTheme(themePreference);
  
  const navigate = useNavigate();
  const params = useParams<{ tenantId?: string }>();
  const { login, guestLogin } = useAuthStore();
  const { fetchPermissions, clearPermissions } = usePermissionStore();

  // 进入登录页时清除 localStorage 中的认证数据
  useEffect(() => {
    localStorage.removeItem('eb-token');
    localStorage.removeItem('eb-user');
    localStorage.removeItem('eb-tenant-id');
    clearPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  // 验证租户是否存在
  useEffect(() => {
    const verifyTenant = async () => {
      const tenantId = params.tenantId;
      if (!tenantId) {
        // 默认租户
        setTenantInfo({ exists: true, id: 'default', display_name: '企业大脑' });
        return;
      }
      
      setTenantLoading(true);
      try {
        const result = await checkTenantExists(tenantId);
        setTenantInfo(result);
        if (!result.exists) {
          setError(`企业 "${tenantId}" 不存在`);
        }
      } catch (err) {
        setTenantInfo({ exists: false, id: tenantId });
        setError('无法验证企业信息');
      } finally {
        setTenantLoading(false);
      }
    };
    
    verifyTenant();
  }, [params.tenantId]);

  // 应用主题
  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    document.documentElement.dataset.theme = resolved;
    try {
      localStorage.setItem('eb-theme-pref', themePreference);
      localStorage.setItem('eb-theme', resolved);
    } catch { /* ignore */ }
  }, [themePreference]);

  // 切换主题
  const cycleTheme = () => {
    setThemePreference(prev => {
      if (prev === 'system') return 'light';
      if (prev === 'light') return 'dark';
      return 'system';
    });
  };

  // 游客登录
  const handleGuestLogin = async () => {
    if (tenantInfo && !tenantInfo.exists) {
      setError(`企业 "${params.tenantId}" 不存在，无法进行游客登录`);
      return;
    }
    
    setError('');
    setLoading(true);
    try {
      clearPermissions();
      await guestLogin();
      await fetchPermissions();
      const target = params.tenantId ? `/${params.tenantId}/apps` : '/apps';
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '游客登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 表单登录
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      clearPermissions();
      const user = await login(email, password);
      await fetchPermissions();
      
      // 根据用户实际所属的租户跳转，而不是 URL 中的租户
      const userTenantId = user.tenant_id;
      let target: string;
      if (userTenantId && userTenantId !== 'default') {
        target = `/${userTenantId}/apps`;
      } else {
        target = '/apps';
      }
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 主题图标
  const ThemeIcon = () => {
    if (themePreference === 'system') {
      return (
        <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
          <path d="M2 3h16v11H2V3Zm8 11v4m-4 0h8"/>
        </svg>
      );
    }
    if (resolvedTheme === 'dark') {
      return (
        <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
          <path d="M17 11A8 8 0 0 1 9 3a8 8 0 1 0 8 8Z"/>
        </svg>
      );
    }
    return (
      <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
        <path d="M10 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-13v2m0 14v2M1 10h2m14 0h2M3 3l2 2m10 10 2 2M3 17l2-2M15 5l2-2"/>
      </svg>
    );
  };

  // 计算显示名称（仅用于验证租户是否存在，标题统一显示"企业大脑"）
  const displayName = '企业大脑';
  const isSpecificTenant = params.tenantId && params.tenantId !== 'default';
  const tenantValid = !tenantInfo || tenantInfo.exists;

  // 租户验证中时显示加载状态
  if (tenantLoading) {
    return (
      <div id="loginPage">
        <div className="login-brand">
          <span>企业大脑</span>
        </div>
        <section className="login-panel">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ color: 'var(--text-2, #666)' }}>验证企业信息...</div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div id="loginPage">
      {/* 品牌标识 */}
      <div className="login-brand">
        <span>{displayName}</span>
      </div>

      {/* 主题切换按钮 */}
      <button 
        className="login-theme btn btn-icon" 
        onClick={cycleTheme}
        aria-label="外观设置"
        title={themePreference === 'system' ? '自动' : themePreference === 'dark' ? '深色' : '浅色'}
      >
        <ThemeIcon />
      </button>

      {/* 登录面板 */}
      <section className="login-panel">
        <h1>欢迎使用{displayName}</h1>
        <p className="login-subtitle">登录你的企业工作空间</p>

        {/* 游客登录按钮 */}
        <button 
          type="button" 
          className="btn btn-secondary login-demo-button"
          onClick={handleGuestLogin}
          disabled={loading || !tenantValid}
          title={!tenantValid ? '企业不存在，无法登录' : '以游客身份浏览'}
        >
          <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" style={{ marginRight: '6px' }}>
            <circle cx="10" cy="6" r="4"/>
            <path d="M2 18c0-4.5 3.5-8 8-8s8 3.5 8 8"/>
          </svg>
          游客登录
        </button>

        {/* 分隔线 */}
        <div className="login-divider">或使用工作邮箱</div>

        {/* 登录表单 */}
        <form id="loginForm" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="loginEmail">工作邮箱</label>
            <input 
              id="loginEmail" 
              name="email" 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username" 
              required 
            />
          </div>

          <div className="field">
            <label htmlFor="loginPassword">密码</label>
            <div className="password-wrap">
              <input 
                id="loginPassword" 
                name="password" 
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" 
                required 
              />
              <button 
                type="button" 
                className="btn btn-icon" 
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? '隐藏密码' : '显示密码'}
              >
                <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                  {showPassword ? (
                    <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6Zm8 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM3 3l14 14"/>
                  ) : (
                    <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6Zm8 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <label className="login-remember">
            <input 
              type="checkbox" 
              id="rememberMe" 
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            记住登录状态
          </label>

          {error && <p id="loginError" className="login-error" role="alert">{error}</p>}

          <button 
            type="submit" 
            id="loginSubmitBtn" 
            className="btn btn-primary login-submit"
            disabled={loading}
          >
            {loading ? '登录中...' : '登录工作空间'}
          </button>
        </form>
      </section>

      {/* 页脚 */}
      <footer className="login-footer">{displayName}</footer>
    </div>
  );
}
